import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  CalendarClock, Download, Plus, Search, Loader2, RotateCcw,
  AlertTriangle, Package, Truck, MapPin, CheckCircle2, ChevronRight, X, Upload, XCircle,
} from 'lucide-react';
import { api, downloadBlob } from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import { MultiSelectFilter } from '@/components/ui/MultiSelectFilter';
import { STATUS_LABEL, ALL_SHIPPED_STATUS_IDS } from '@/features/orders/constants';
import { ImportAppointmentsModal } from './ImportAppointmentsModal';

interface Merchant { id: number; companyName: string; brandName?: string }
interface Appt {
  id: number; lrNo: string; merchantId?: number;
  merchant?: { id: number; companyName: string; brandName?: string };
  appointmentDate?: string; appointmentTime?: string; poNumber?: string; poExpiryDate?: string;
  appointmentRef?: string; statusId?: number | null; statusLabel?: string | null;
  notDelivered?: boolean; poExpired?: boolean; urgent?: boolean; createdAt?: string;
  /** Raised automatically at booking; still needs its date and PO. */
  pending?: boolean;
  /** Which tab this row belongs to, decided by the server so the grid and the counts agree. */
  bucket?: 'pending' | 'failed' | 'booked';
  eligibleForAppointment?: boolean;
  /** Why it does not need a slot — captured when No is chosen. */
  notRequiredRemarks?: string | null;
  /** Being arranged outside the carrier — porter service, the consignee's own transporter. */
  thirdParty?: boolean;
  thirdPartyTitle?: string | null;
  thirdPartyDate?: string | null;
  thirdPartyTime?: string | null;
  thirdPartyRemarks?: string | null;
  slotPassed?: boolean;
  boxes?: number;
  consigneeName?: string | null; consigneeCity?: string | null; consigneePincode?: string | null;
  courierName?: string | null; courierId?: number | null; courierApi?: boolean | null; edd?: string | null;
  /** The automatic carrier push: SENT / FAILED / SKIPPED, with the courier's reason when refused. */
  courierPushStatus?: string | null; courierPushError?: string | null; courierPushAt?: string | null; courierPushAttempts?: number;
  order?: { id?: string; orderNo?: string; awbNo?: string; statusId?: number | null; edd?: string | null };
}
interface Summary {
  expiredYesterday: number; expiringToday: number; expiringTomorrow: number; urgentNotDelivered: number; pending?: number;
  tabs?: { pending: number; failed: number; booked: number; bookedToday: number; notRequired: number; thirdParty: number; all: number };
  /** Couriers that appear on this screen, busiest first; `api` = booked through Delhivery's API. */
  couriers?: Array<{ id: number; name: string; count: number; api: boolean }>;
}

/**
 * The only delivery windows Delhivery LTL accepts — their API refuses anything else and answers with
 * this exact list. Shown as a dropdown so nobody can type a slot that will be rejected.
 */
export const APPOINTMENT_SLOTS = [
  '12:00 AM-07:00 AM',
  '07:00 AM-10:00 AM',
  '09:00 AM-12:00 PM',
  '12:00 PM-03:00 PM',
  '03:00 PM-06:00 PM',
  '06:00 PM-09:00 PM',
  '09:00 PM-11:59 PM',
] as const;

const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null);
const fmtShort = (d?: string | null) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : null);

/**
 * Manage Appointments.
 *
 * Built around the three questions the desk actually works through, as tabs rather than filters:
 * what still needs a slot (**Pending**), what is going wrong (**At risk** — undelivered with an
 * expired PO or a slot already gone), and what is agreed (**Booked**). The counts and the rows come
 * from the same server-side rule, so a tab can never show a number the list disagrees with.
 *
 * A row is a CONSIGNMENT, not a form: the LR with its box count, who it is going to and where, when
 * the PO dies, where the shipment currently is and when it was promised. Everything else — the
 * carrier push, the PO fields — stays inside the booking modal, which is shared with the B2B order
 * page and is deliberately untouched here.
 */
const TABS = [
  { id: 'pending', label: 'Pending' },
  // Booked splits in two: what the desk works from today, and the whole book.
  { id: 'booked-today', label: "Booked — Today" },
  { id: 'booked', label: 'Booked — All' },
  // Replaces "At risk": the carrier refused the slot, or the PO / booked day has run out.
  { id: 'failed', label: 'Appointment Failed' },
  // Marked "Appointment Required: No" — off every working list, kept in one place.
  { id: 'not-required', label: 'Appointment not required' },
  // Somebody else is arranging the slot — off the working lists, kept in one place with who and when.
  { id: 'third-party', label: 'Third Party Appointments' },
  { id: 'all', label: 'All' },
] as const;

/**
 * Tabs that carry the Third Party switch. Booked — All and Appointment Failed were added on the
 * operator's request (19-10): a slot already booked, or one the carrier refused / that ran out, is
 * often handed to a porter service or the consignee's own transporter at exactly that point.
 * Setting Yes moves the row to the Third Party tab like it does from Pending.
 */
const THIRD_PARTY_TABS = ['pending', 'booked', 'failed', 'third-party', 'all'];

export function AppointmentsPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<string>('pending');
  const [askThirdParty, setAskThirdParty] = useState<Appt | null>(null);
  const [merchantId, setMerchantId] = useState('');
  // Arriving from a tracking row ("Appointment: 12345") lands here with ?lrNo= on the URL, so the
  // screen opens already narrowed to that consignment — and on All, because that LR may sit in any
  // of the three tabs and an empty Pending tab would read as "no such appointment".
  const initialLr = new URLSearchParams(window.location.search).get('lrNo')?.trim() ?? '';
  const [lrNo, setLrNo] = useState(initialLr);
  const [consignee, setConsignee] = useState('');
  const [city, setCity] = useState('');
  const [statusIds, setStatusIds] = useState<(string | number)[]>([]);
  const [courierIds, setCourierIds] = useState<(string | number)[]>([]);
  const [eddFrom, setEddFrom] = useState('');
  const [eddTo, setEddTo] = useState('');
  const [expiredPo, setExpiredPo] = useState(false);
  // '' = both | 'yes' | 'no' — does this consignment need a delivery slot at all?
  const [eligible, setEligible] = useState('');
  const [askNo, setAskNo] = useState<Appt | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [showBook, setShowBook] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [showImport, setShowImport] = useState(false);
  // What the courier answered for each LR sent with "Book selected".
  const [bookResult, setBookResult] = useState<{ booked: number; failed: number; results: Array<{ lrNo: string; ok: boolean; recorded?: boolean; appointmentRef?: string | null; message: string }> } | null>(null);
  const [showBanner, setShowBanner] = useState(true);
  // Set when Book is pressed on a row, so the modal opens on that LR.
  const [bookLr, setBookLr] = useState<string | undefined>(undefined);
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => { if (initialLr) setTab('all'); /* eslint-disable-next-line */ }, []);

  const { data: merchantsData } = useQuery({
    queryKey: ['merchants-light'],
    queryFn: async () => (await api.get('/merchants', { params: { pageSize: 500 } })).data,
  });
  const merchants: Merchant[] = merchantsData?.items ?? merchantsData ?? [];

  const { data: summary } = useQuery<Summary>({
    queryKey: ['appointments-summary'],
    queryFn: async () => (await api.get('/appointments/summary')).data,
  });

  const params = useMemo(() => {
    const q: any = { page, pageSize, tab };
    if (merchantId) q.merchantId = merchantId;
    if (lrNo.trim()) q.lrNo = lrNo.trim();
    if (consignee.trim()) q.consignee = consignee.trim();
    if (city.trim()) q.city = city.trim();
    if (statusIds.length) q.statusIds = statusIds.join(',');
    if (courierIds.length) q.courierIds = courierIds.join(',');
    if (eddFrom) q.eddFrom = eddFrom;
    if (eddTo) q.eddTo = eddTo;
    if (expiredPo) q.expiredPo = '1';
    if (eligible) q.eligible = eligible;
    return q;
  }, [page, pageSize, tab, merchantId, lrNo, consignee, city, statusIds, courierIds, eddFrom, eddTo, expiredPo, eligible]);

  const { data: list, isLoading } = useQuery({
    queryKey: ['appointments', params],
    queryFn: async () => (await api.get<{ total: number; items: Appt[] }>('/appointments', { params })).data,
  });

  // Send each ticked LR's OWN saved appointment (date, slot, PO) to its courier. Different from the
  // "same slot" modal, which types one slot over many LRs.
  const bookSelected = useMutation({
    mutationFn: async (lrNos: string[]) => (await api.post('/appointments/book-selected', { lrNos }, { timeout: 300_000 })).data,
    onSuccess: (d: any) => {
      setBookResult(d);
      if (d.failed) toast.warning(`${d.booked} booked, ${d.failed} not booked — see the reasons.`);
      else toast.success(`All ${d.booked} booked.`);
      refresh();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Booking failed'),
  });

  /** Hand a consignment to a third party, or take it back. Mirrors `setEligibility`. */
  const setThirdParty = useMutation({
    mutationFn: async (v: { id: number; thirdParty: boolean; title?: string; date?: string; time?: string; remarks?: string }) =>
      (await api.patch(`/appointments/${v.id}/third-party`, v)).data,
    onSuccess: (r: any) => { toast.success(r?.thirdParty ? 'Moved to Third Party Appointments' : 'Back on our own list'); refresh(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not update'),
  });

  const setEligibility = useMutation({
    mutationFn: async (v: { id: number; eligible: boolean; remarks?: string }) =>
      (await api.patch(`/appointments/${v.id}/eligibility`, { eligible: v.eligible, remarks: v.remarks })).data,
    onSuccess: (r: any) => { toast.success(r?.eligibleForAppointment ? 'Marked as needing an appointment' : 'Marked as not needing an appointment'); refresh(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not update'),
  });

  function refresh() {
    qc.invalidateQueries({ queryKey: ['appointments'] });
    qc.invalidateQueries({ queryKey: ['appointments-summary'] });
    setSelected([]);
  }
  function reset() {
    setMerchantId(''); setLrNo(''); setConsignee(''); setCity(''); setStatusIds([]); setCourierIds([]);
    setEddFrom(''); setEddTo(''); setExpiredPo(false); setEligible(''); setPage(1);
  }

  function exportCsv() {
    const p = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v != null && v !== '' && k !== 'page' && k !== 'pageSize') p.set(k, String(v)); });
    const qsp = p.toString();
    downloadBlob('/appointments/export' + (qsp ? `?${qsp}` : ''), `appointments-${new Date().toISOString().slice(0, 10)}.csv`);
  }

  const items = list?.items ?? [];
  const total = list?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const firstRow = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastRow = Math.min(page * pageSize, total);
  const counts = summary?.tabs;
  const countFor = (id: string) =>
    id === 'pending' ? counts?.pending
      : id === 'failed' ? counts?.failed
      : id === 'booked-today' ? counts?.bookedToday
      : id === 'booked' ? counts?.booked
      : id === 'third-party' ? counts?.thirdParty
      : id === 'not-required' ? counts?.notRequired
      : counts?.all;

  const allSelected = items.length > 0 && items.every((r) => selected.includes(r.lrNo));
  const toggleAll = () => setSelected(allSelected ? [] : Array.from(new Set(items.map((r) => r.lrNo))));
  const toggleOne = (lr: string) => setSelected((s) => (s.includes(lr) ? s.filter((x) => x !== lr) : [...s, lr]));

  const statusOptions = ALL_SHIPPED_STATUS_IDS.map((id) => ({ value: id, label: STATUS_LABEL[id] ?? `Status ${id}`, hint: '#' + id }));
  // Delhivery (booked through its API) and our own couriers live side by side here — the hint says which.
  const courierOptions = (summary?.couriers ?? []).map((c) => ({
    value: c.id, label: `${c.name} (${c.count})`, hint: c.api ? 'Delhivery API' : 'Own / manual',
  }));

  return (
    <div className="p-6 max-w-[1500px] mx-auto">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><CalendarClock size={22} /> Manage Appointments</h1>
          <p className="text-sm text-slate-500">Delivery slots and PO validity for every B2B consignment that needs one</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} className="px-3 py-2 border border-slate-200 rounded-lg text-sm flex items-center gap-1.5 hover:bg-slate-50">
            <Download size={14} /> Export
          </button>
          <button onClick={() => setShowImport(true)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm flex items-center gap-1.5 hover:bg-slate-50">
            <Upload size={14} /> Import appointments
          </button>
          <button onClick={() => { setBookLr(undefined); setShowBook(true); }}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm flex items-center gap-1.5 hover:bg-slate-50">
            <Plus size={14} /> Book an appointment
          </button>
          <button onClick={() => setShowBulk(true)}
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-1.5 hover:bg-primary/90">
            <Plus size={14} /> Create bulk appointment
          </button>
        </div>
      </div>

      {/* Tabs — the three states of a consignment waiting on a slot. */}
      <div className="flex items-center gap-1 border-b border-slate-200 mb-4">
        {TABS.map((t) => {
          const n = countFor(t.id);
          const on = tab === t.id;
          return (
            <button key={t.id} onClick={() => { setTab(t.id); setPage(1); setSelected([]); }}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition flex items-center gap-2 ${on ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
              {t.label}
              {n != null && (
                <span className={`px-1.5 py-0.5 rounded-full text-[11px] ${t.id === 'failed' && n > 0 ? 'bg-red-100 text-red-700' : on ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-600'}`}>{n}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 mb-3">
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2 items-end">
          <div className="md:col-span-1">
            <label className="text-[11px] font-medium text-slate-500 mb-1 block">LR number</label>
            <div className="relative">
              <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={lrNo} onChange={(e) => { setLrNo(e.target.value); setPage(1); }} placeholder="Search LR" className="input w-full font-mono pl-7" />
            </div>
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-500 mb-1 block">Consignee</label>
            <input value={consignee} onChange={(e) => { setConsignee(e.target.value); setPage(1); }} placeholder="Name" className="input w-full" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-500 mb-1 block">City</label>
            <input value={city} onChange={(e) => { setCity(e.target.value); setPage(1); }} placeholder="City" className="input w-full" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-500 mb-1 block">Shipment status</label>
            <MultiSelectFilter label="" placeholder="Any status" values={statusIds}
              onChange={(v) => { setStatusIds(v); setPage(1); }} options={statusOptions} />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-500 mb-1 block">Courier</label>
            <MultiSelectFilter label="" placeholder="All couriers" values={courierIds}
              onChange={(v) => { setCourierIds(v); setPage(1); setSelected([]); }} options={courierOptions} />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-500 mb-1 block">Merchant</label>
            <select value={merchantId} onChange={(e) => { setMerchantId(e.target.value); setPage(1); }} className="input w-full">
              <option value="">All merchants</option>
              {merchants.map((m) => (<option key={m.id} value={m.id}>#{m.id} · {m.companyName}</option>))}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-500 mb-1 block">Delivery by (from)</label>
            <input type="date" value={eddFrom} onChange={(e) => { setEddFrom(e.target.value); setPage(1); }} className="input w-full" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-500 mb-1 block">Delivery by (to)</label>
            <input type="date" value={eddTo} onChange={(e) => { setEddTo(e.target.value); setPage(1); }} className="input w-full" />
          </div>
        </div>
        <div className="flex items-center gap-3 mt-2.5">
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={expiredPo} onChange={(e) => { setExpiredPo(e.target.checked); setPage(1); }} className="rounded" />
            Show only expired POs
          </label>
          <select value={eligible} onChange={(e) => { setEligible(e.target.value); setPage(1); }}
            className="input py-1.5 text-xs" title="Does this consignment need a delivery slot?">
            <option value="">Eligible: any</option>
            <option value="yes">Eligible: Yes</option>
            <option value="no">Eligible: No</option>
          </select>
          <button onClick={reset} className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs flex items-center gap-1.5"><RotateCcw size={12} /> Reset</button>
          {selected.length > 0 && (
            <>
              {/* Each ticked LR goes with its OWN saved date, slot and PO (imported or typed). */}
              <button onClick={() => bookSelected.mutate(selected)} disabled={bookSelected.isPending}
                title="Send each ticked appointment to its courier with its own saved date, slot and PO"
                className="px-3 py-1.5 bg-primary text-white rounded-lg text-xs flex items-center gap-1.5 disabled:opacity-50">
                {bookSelected.isPending ? <Loader2 size={12} className="animate-spin" /> : <CalendarClock size={12} />} Book {selected.length} selected
              </button>
              <button onClick={() => setShowBulk(true)} className="px-3 py-1.5 border border-slate-300 text-slate-700 rounded-lg text-xs flex items-center gap-1.5 hover:bg-slate-50"
                title="Type one date, slot and PO for all the ticked LRs">
                Same slot for {selected.length} selected
              </button>
            </>
          )}
          <div className="ml-auto text-xs text-slate-500">{total.toLocaleString('en-IN')} consignment{total === 1 ? '' : 's'}</div>
        </div>
      </div>

      {showBanner && (
        <div className="mb-3 rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-2.5 flex items-start gap-3">
          <div className="text-sm text-emerald-900">
            <div className="font-medium">Appointments are raised automatically for the consignees on your list.</div>
            <div className="text-xs text-emerald-800/80">
              Settings → Appointments Customer holds the names (Blinkit, Zepto, Flipkart…). Every B2B booking to one of them
              appears here as Pending — all it needs from you is the slot and the PO details.
            </div>
          </div>
          <button onClick={() => setShowBanner(false)} className="ml-auto text-emerald-700 hover:text-emerald-900"><X size={16} /></button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2.5 w-8"><input type="checkbox" checked={allSelected} onChange={toggleAll} className="rounded" /></th>
                <th className="text-left px-3 py-2.5">LR details</th>
                <th className="text-left px-3 py-2.5">Courier</th>
                <th className="text-left px-3 py-2.5">Consignee details</th>
                <th className="text-left px-3 py-2.5">Appointment</th>
                <th className="text-left px-3 py-2.5">PO expiry date</th>
                <th className="text-left px-3 py-2.5">Shipment status</th>
                <th className="text-left px-3 py-2.5">Appointment required</th>
                {/* Only where the decision is made and where the result is read. Elsewhere it is a
                    column of "No" that tells nobody anything. */}
                {THIRD_PARTY_TABS.includes(tab) && (
                  <th className="text-left px-3 py-2.5">Third party</th>
                )}
                <th className="text-left px-3 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (<tr><td colSpan={10} className="text-center py-10 text-slate-400"><Loader2 size={20} className="mx-auto animate-spin" /></td></tr>)}
              {!isLoading && items.length === 0 && (
                <tr><td colSpan={10} className="text-center py-12 text-slate-400">
                  {tab === 'pending' ? 'Nothing is waiting for a slot.' : tab === 'failed' ? 'No failed appointments — every PO is valid, every slot is still ahead and no carrier has refused one.' : tab === 'booked-today' ? 'Nothing is booked for today.' : tab === 'third-party' ? 'Nothing handed to a third party yet. Set Third Party to Yes on the Pending, Booked — All or Appointment Failed tab and the row moves here.' : tab === 'not-required' ? 'Nothing marked as not needing an appointment. Set a row to No on any other tab and it moves here.' : 'No appointments for these filters.'}
                </td></tr>
              )}
              {items.map((r) => {
                const atRisk = r.bucket === 'failed';
                return (
                  <tr key={r.id} className={`border-t border-slate-100 ${atRisk ? 'bg-red-50/60 hover:bg-red-50' : 'hover:bg-slate-50/60'}`}>
                    <td className="px-3 py-2.5 align-top">
                      <input type="checkbox" checked={selected.includes(r.lrNo)} onChange={() => toggleOne(r.lrNo)} className="rounded" />
                    </td>
                    <td className="px-3 py-2.5 align-top">
                      <div className="font-mono font-medium text-slate-900 flex items-center gap-1">
                        {atRisk && <AlertTriangle size={13} className="text-red-600" />}{r.lrNo}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {r.boxes ?? 1} box{(r.boxes ?? 1) === 1 ? '' : 'es'}
                        {r.merchant?.companyName ? ` · ${r.merchant.companyName}` : ''}
                      </div>
                    </td>
                    {/* Which courier — and so HOW it gets booked: Delhivery through its API, our own
                        couriers by the team agreeing the slot and recording it here. */}
                    <td className="px-3 py-2.5 align-top text-xs">
                      {r.courierName ? (
                        <>
                          <div className="text-slate-800 font-medium flex items-center gap-1"><Truck size={12} className="text-slate-400" />{r.courierName}</div>
                          <span className={`inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${r.courierApi ? 'bg-sky-100 text-sky-700' : 'bg-amber-50 text-amber-700'}`}>
                            {r.courierApi ? 'Delhivery · API' : 'Own courier'}
                          </span>
                        </>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2.5 align-top">
                      <div className="text-slate-800">{r.consigneeName || '—'}</div>
                      <div className="text-[11px] text-slate-500 flex items-center gap-1">
                        <MapPin size={10} />{[r.consigneeCity, r.consigneePincode].filter(Boolean).join(', ') || '—'}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 align-top text-xs">
                      {r.appointmentDate ? (
                        <>
                          <div className="text-slate-800 font-medium">{fmtDate(r.appointmentDate)}{r.appointmentTime ? ` · ${r.appointmentTime}` : ''}</div>
                          {r.appointmentRef && <div className="text-[11px] text-slate-500 font-mono">No. {r.appointmentRef}</div>}
                          {r.courierPushStatus === 'SENT' && (
                            <div className="text-[10px] text-emerald-700">Booked with the courier</div>
                          )}
                          {r.courierPushStatus === 'RECORDED' && (
                            <div className="text-[10px] text-emerald-700">Booked by our team (own courier)</div>
                          )}
                          {r.courierPushStatus === 'READY' && (
                            <div className="text-[10px] mt-0.5"><span className="px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-medium">Ready</span>
                              <span className="text-slate-500"> — tick it and press Book selected</span></div>
                          )}
                          {r.courierPushStatus === 'FAILED' && (
                            <div className="text-[10px] text-red-600 mt-0.5 max-w-[220px]" title={r.courierPushError ?? ''}>
                              Courier refused: {(r.courierPushError ?? '').slice(0, 60)}{(r.courierPushError ?? '').length > 60 ? '…' : ''}
                            </div>
                          )}
                          {r.slotPassed && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">slot passed</span>}
                        </>
                      ) : (
                        <>
                          <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[11px] font-medium">Not booked</span>
                          {/* Why the automatic request did not stick, in the courier's own words —
                              otherwise "Not booked" looks like nobody has tried. */}
                          {r.courierPushStatus === 'FAILED' && (
                            <div className="text-[10px] text-red-600 mt-0.5 max-w-[220px]" title={r.courierPushError ?? ''}>
                              Courier refused: {(r.courierPushError ?? '').slice(0, 60)}{(r.courierPushError ?? '').length > 60 ? '…' : ''}
                            </div>
                          )}
                          {r.courierPushStatus === 'WAITING_PO' && (
                            <div className="text-[10px] text-amber-700 mt-0.5">Waiting for the PO number</div>
                          )}
                          {r.courierPushStatus === 'SKIPPED' && (
                            <div className="text-[10px] text-slate-500 mt-0.5 max-w-[220px]">Own / manual courier — agree the slot with the consignee, then record it here</div>
                          )}
                        </>
                      )}
                    </td>
                    <td className="px-3 py-2.5 align-top text-xs">
                      {r.poExpiryDate
                        ? <span className={r.poExpired ? 'text-red-600 font-medium' : 'text-slate-700'}>{fmtDate(r.poExpiryDate)}{r.poExpired ? ' · expired' : ''}</span>
                        : <span className="text-red-600 font-medium">PO missing</span>}
                      {r.poNumber && <div className="text-[11px] text-slate-500 truncate max-w-[180px]" title={r.poNumber}>{r.poNumber}</div>}
                    </td>
                    <td className="px-3 py-2.5 align-top text-xs">
                      {r.statusLabel
                        ? <span className={`px-1.5 py-0.5 rounded text-[11px] ${r.notDelivered ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>{r.statusLabel}</span>
                        : <span className="text-slate-400">—</span>}
                      {r.edd && <div className="text-[11px] text-slate-500 mt-0.5">Delivery by {fmtShort(r.edd)}</div>}
                    </td>
                    <td className="px-3 py-2.5 align-top whitespace-nowrap">
                      {/* Yes / No, shown as the two choices rather than a toggle you have to guess
                          at. Choosing No takes the row off every working list and into the
                          "Appointment not required" tab; Yes brings it back. */}
                      <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
                        {([true, false] as const).map((v) => {
                          const on = (r.eligibleForAppointment !== false) === v;
                          return (
                            <button key={String(v)} onClick={() => {
                              if (on) return;
                              // Saying No takes the row off every working list, so it asks WHY —
                              // a month later nobody remembers, and the tab is where they look.
                              if (!v) { setAskNo(r); return; }
                              setEligibility.mutate({ id: r.id, eligible: true });
                            }}
                              disabled={setEligibility.isPending}
                              title={v ? 'This consignment needs a delivery slot' : 'Not required — move to "Appointment not required"'}
                              className={`px-2 py-0.5 text-[11px] transition disabled:opacity-50 ${on
                                ? (v ? 'bg-emerald-600 text-white' : 'bg-slate-600 text-white')
                                : 'bg-white text-slate-500 hover:bg-slate-50'}`}>
                              {v ? 'Yes' : 'No'}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                    {THIRD_PARTY_TABS.includes(tab) && (
                      <td className="px-3 py-2.5 align-top whitespace-nowrap">
                        {/* Default is No. Yes asks who is handling it and when, then the row leaves
                            the working lists for the Third Party tab — the same shape as
                            "Appointment required", because it is the same kind of decision. */}
                        <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
                          {([true, false] as const).map((v) => {
                            const on = !!r.thirdParty === v;
                            return (
                              <button key={String(v)} onClick={() => {
                                if (on) return;
                                if (v) { setAskThirdParty(r); return; }
                                setThirdParty.mutate({ id: r.id, thirdParty: false });
                              }}
                                disabled={setThirdParty.isPending}
                                title={v ? 'Someone outside the carrier is arranging this slot' : 'We are arranging this slot'}
                                className={`px-2 py-0.5 text-[11px] transition disabled:opacity-50 ${on
                                  ? (v ? 'bg-indigo-600 text-white' : 'bg-slate-600 text-white')
                                  : 'bg-white text-slate-500 hover:bg-slate-50'}`}>
                                {v ? 'Yes' : 'No'}
                              </button>
                            );
                          })}
                        </div>
                        {/* What was agreed, on the tab where people come to read it. */}
                        {r.thirdParty && (
                          <div className="text-[11px] text-slate-600 mt-1 max-w-[190px]">
                            <div className="font-medium text-slate-800 truncate" title={r.thirdPartyTitle ?? ''}>{r.thirdPartyTitle}</div>
                            <div>{fmtDate(r.thirdPartyDate)}{r.thirdPartyTime ? ` · ${r.thirdPartyTime}` : ''}</div>
                            {r.thirdPartyRemarks && <div className="text-slate-500 mt-0.5 whitespace-pre-wrap">{r.thirdPartyRemarks}</div>}
                          </div>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-2.5 align-top whitespace-nowrap">
                      <button onClick={() => { setBookLr(r.lrNo); setShowBook(true); }}
                        className="text-primary hover:underline text-xs font-medium inline-flex items-center gap-0.5">
                        {r.appointmentDate ? 'Update appointment' : 'Book appointment'} <ChevronRight size={12} />
                      </button>

                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-100 text-xs text-slate-600">
          <div>Showing {firstRow}-{lastRow} of {total.toLocaleString('en-IN')}</div>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-2 py-1 border border-slate-200 rounded disabled:opacity-40">Previous</button>
            <span>Page {page} of {lastPage}</span>
            <button disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)} className="px-2 py-1 border border-slate-200 rounded disabled:opacity-40">Next</button>
          </div>
          <div className="flex items-center gap-2">
            Rows per page
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className="input py-1 text-xs">
              {[20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
      </div>

      <BookAppointmentModal isOpen={showBook} onClose={() => setShowBook(false)} lockedLrNo={bookLr} onSaved={refresh} />
      <BulkAppointmentModal isOpen={showBulk} onClose={() => setShowBulk(false)} initialLrNos={selected} onSaved={refresh} />
      <ImportAppointmentsModal isOpen={showImport} onClose={() => setShowImport(false)} onChanged={refresh} slots={APPOINTMENT_SLOTS} />
      {askThirdParty && (
        <ThirdPartyDialog row={askThirdParty} busy={setThirdParty.isPending} fromTab={tab}
          onCancel={() => setAskThirdParty(null)}
          onConfirm={(v) => { const r = askThirdParty; setAskThirdParty(null); setThirdParty.mutate({ id: r.id, thirdParty: true, ...v }); }} />
      )}
      {askNo && (
        <NotRequiredDialog row={askNo} busy={setEligibility.isPending}
          onCancel={() => setAskNo(null)}
          onConfirm={(remarks) => { const r = askNo; setAskNo(null); setEligibility.mutate({ id: r.id, eligible: false, remarks }); }} />
      )}
      <Modal isOpen={!!bookResult} onClose={() => setBookResult(null)} title="Book selected — courier replies"
        subtitle={bookResult ? `${bookResult.booked} booked · ${bookResult.failed} not booked` : ''} maxWidth="3xl">
        <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100 text-sm">
          {(bookResult?.results ?? []).map((x) => (
            <div key={x.lrNo} className="py-2 flex items-start gap-2">
              {x.ok ? <CheckCircle2 size={15} className="text-emerald-600 mt-0.5 shrink-0" /> : <XCircle size={15} className="text-red-600 mt-0.5 shrink-0" />}
              <div>
                <div className="font-mono font-medium text-slate-900">{x.lrNo}</div>
                <div className={x.ok ? 'text-emerald-700 text-xs' : 'text-red-600 text-xs'}>{x.message}</div>
                {x.ok && x.appointmentRef && <div className="text-[11px] text-slate-500 font-mono">Ref {x.appointmentRef}</div>}
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}

/**
 * One slot, many LRs.
 *
 * A consignee gives a single appointment for a whole truck, so the desk was opening the booking
 * modal once per consignment and typing the same date, slot and PO every time. Each LR still goes
 * through the same save as a single booking — same validation, same optional carrier push — and each
 * reports its own result, because one bad LR must not take the other forty down with it.
 */
function BulkAppointmentModal({
  isOpen, onClose, initialLrNos, onSaved,
}: { isOpen: boolean; onClose: () => void; initialLrNos: string[]; onSaved?: () => void }) {
  const [text, setText] = useState('');
  const [form, setForm] = useState({ appointmentDate: '', appointmentTime: '', poNumber: '', poExpiryDate: '', appointmentRef: '' });
  const [pushToCourier, setPushToCourier] = useState(false);
  const [result, setResult] = useState<any>(null);

  useEffect(() => {
    if (!isOpen) return;
    setText(initialLrNos.join('\n'));
    setForm({ appointmentDate: '', appointmentTime: '', poNumber: '', poExpiryDate: '', appointmentRef: '' });
    setPushToCourier(false);
    setResult(null);
  }, [isOpen, initialLrNos]);

  const lrNos = Array.from(new Set(text.split(/[\s,;]+/).map((v) => v.trim()).filter(Boolean)));

  const save = useMutation({
    mutationFn: async () => (await api.post('/appointments/bulk', { lrNos, ...form, pushToCourier })).data,
    onSuccess: (d: any) => {
      setResult(d);
      if (d.ok) toast.success(`${d.ok} appointment${d.ok === 1 ? '' : 's'} saved${d.failed ? `, ${d.failed} failed` : ''}`);
      else toast.error('No appointment could be saved — see the list below');
      onSaved?.();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Bulk booking failed'),
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create bulk appointment"
      subtitle="One date, slot and PO applied to every LR you paste below" maxWidth="2xl">
      <div className="space-y-4">
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">LR numbers ({lrNos.length})</label>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4}
            placeholder="Paste LR numbers — one per line, or separated by commas/spaces"
            className="input w-full font-mono text-xs resize-y" />
          <div className="text-[11px] text-slate-400 mt-0.5">Up to 200 at a time. Tick rows on the list first and they arrive here already filled in.</div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Appointment Date *</label>
            <input type="date" value={form.appointmentDate} onChange={(e) => setForm((f) => ({ ...f, appointmentDate: e.target.value }))} className="input w-full" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Appointment Slot</label>
            <select value={form.appointmentTime} onChange={(e) => setForm((f) => ({ ...f, appointmentTime: e.target.value }))} className="input w-full">
              <option value="">Select a slot</option>
              {APPOINTMENT_SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">PO Number</label>
            <input value={form.poNumber} onChange={(e) => setForm((f) => ({ ...f, poNumber: e.target.value }))} className="input w-full" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">PO Expiry Date</label>
            <input type="date" value={form.poExpiryDate} onChange={(e) => setForm((f) => ({ ...f, poExpiryDate: e.target.value }))} className="input w-full" />
          </div>
        </div>
        <label className="flex items-start gap-2 rounded-lg border border-indigo-200 bg-indigo-50 p-3 cursor-pointer">
          <input type="checkbox" className="mt-0.5" checked={pushToCourier} onChange={(e) => setPushToCourier(e.target.checked)} />
          <span className="text-xs text-indigo-900">
            <span className="font-medium">Also book each one with the courier</span>
            <span className="block text-indigo-700 mt-0.5">
              Only Delhivery B2B accepts appointments today, and it needs the time AND the PO expiry date. Anything else is
              saved here only.
            </span>
          </span>
        </label>

        {result && (
          <div className="border border-slate-200 rounded-lg p-3 max-h-48 overflow-y-auto text-xs">
            <div className="font-medium text-slate-800 mb-1">{result.ok} saved · {result.failed} failed</div>
            {(result.results ?? []).filter((r: any) => !r.ok).map((r: any) => (
              <div key={r.lrNo} className="text-red-600">{r.lrNo}: {r.error}</div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">CLOSE</button>
          <button onClick={() => save.mutate()} disabled={!lrNos.length || !form.appointmentDate || save.isPending}
            className="px-5 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2">
            {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <CalendarClock size={14} />} Book {lrNos.length || ''} appointment{lrNos.length === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ============ Book / Edit Appointment modal (reused on the B2B order detail page) ============
export function BookAppointmentModal({
  isOpen, onClose, lockedLrNo, onSaved,
}: { isOpen: boolean; onClose: () => void; lockedLrNo?: string; onSaved?: () => void }) {
  const [lrNo, setLrNo] = useState(lockedLrNo ?? '');
  const [card, setCard] = useState<any>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'found' | 'notfound'>('idle');
  const [form, setForm] = useState({ appointmentDate: '', appointmentTime: '', poNumber: '', poExpiryDate: '', appointmentRef: '' });
  const [pushToCourier, setPushToCourier] = useState(true);

  useEffect(() => {
    if (!isOpen) return;
    setLrNo(lockedLrNo ?? '');
    setCard(null); setStatus('idle');
    setPushToCourier(true);
    setForm({ appointmentDate: '', appointmentTime: '', poNumber: '', poExpiryDate: '', appointmentRef: '' });
    if (lockedLrNo) doLookup(lockedLrNo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, lockedLrNo]);

  async function doLookup(lr: string) {
    const v = (lr || '').trim();
    if (!v) { setCard(null); setStatus('idle'); return; }
    setStatus('loading');
    try {
      const r = await api.get('/appointments/order-lookup', { params: { lrNo: v } });
      if (r.data?.found) { setCard(r.data.order); setStatus('found'); }
      else { setCard(null); setStatus('notfound'); }
    } catch { setCard(null); setStatus('notfound'); }
  }

  const save = useMutation({
    mutationFn: async () =>
      (await api.post('/appointments', { lrNo: lrNo.trim(), ...form, pushToCourier: pushToCourier && !!card?.supportsCourierBooking })).data,
    onSuccess: (d: any) => {
      const cb = d?.courierBooking;
      // The row is saved either way, so a carrier failure is a WARNING, not an error — saying
      // "Save failed" would send ops back to re-enter an appointment that is already on file.
      if (cb && cb.ok === false) {
        toast.warning(`Appointment saved, but ${card?.courier?.name ?? 'the courier'} did not accept it: ${cb.errorMessage ?? 'unknown error'}`, { duration: 12000 });
      } else if (cb && cb.ok) {
        toast.success(cb.appointmentId ? `Appointment booked with the courier (ref ${cb.appointmentId})` : 'Appointment booked with the courier');
      } else {
        toast.success('Appointment saved');
      }
      onSaved?.(); onClose();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });

  // Delhivery rejects a booking with no PO expiry, so don't let the request leave without one.
  const pushNeedsPo = pushToCourier && !!card?.supportsCourierBooking && (!form.poExpiryDate || !form.appointmentTime);
  const canSave = status === 'found' && !!form.appointmentDate && !pushNeedsPo && !save.isPending;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Book an Appointment" subtitle="Enter the LR number, confirm the shipment, then fill the appointment details" maxWidth="2xl">
      <div className="space-y-5">
        {/* LR entry */}
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">LR Number *</label>
          <div className="flex gap-2">
            <input
              value={lrNo}
              onChange={(e) => setLrNo(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') doLookup(lrNo); }}
              onBlur={() => { if (!lockedLrNo) doLookup(lrNo); }}
              readOnly={!!lockedLrNo}
              placeholder="Enter LR number and press Enter"
              className={`input flex-1 font-mono ${lockedLrNo ? 'bg-slate-100' : ''}`}
            />
            {!lockedLrNo && (
              <button onClick={() => doLookup(lrNo)} disabled={!lrNo.trim() || status === 'loading'}
                className="px-4 py-2 border border-slate-200 rounded-lg text-sm flex items-center gap-1.5 hover:bg-slate-50 disabled:opacity-50">
                {status === 'loading' ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Fetch
              </button>
            )}
          </div>
          {status === 'notfound' && <div className="text-xs text-amber-600 mt-1">No order found for this LR number.</div>}
        </div>

        {/* Basic info card */}
        {status === 'found' && card && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><Package size={14} /> {card.orderNo} · {card.orderType}</div>
              <span className={`text-[11px] px-2 py-0.5 rounded ${card.notDelivered ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                {card.notDelivered ? <AlertTriangle size={10} className="inline mr-0.5" /> : <CheckCircle2 size={10} className="inline mr-0.5" />}{card.statusLabel}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-xs text-slate-600">
              <Info label="Merchant" value={card.merchant?.companyName} />
              <Info label="Courier" value={card.courier?.name} icon={<Truck size={11} />} />
              <Info label="AWB" value={card.awbNo} />
              <Info label="Consignee" value={card.customerName} />
              <Info label="Destination" value={[card.customerCity, card.customerState].filter(Boolean).join(', ')} icon={<MapPin size={11} />} />
              <Info label="Boxes" value={card.totalBoxes != null ? String(card.totalBoxes) : undefined} />
            </div>
          </div>
        )}

        {/* Appointment fields */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Appointment Date *</label>
            <input type="date" value={form.appointmentDate} onChange={(e) => setForm(f => ({ ...f, appointmentDate: e.target.value }))} className="input w-full" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Appointment Slot *</label>
            <select value={form.appointmentTime} onChange={(e) => setForm(f => ({ ...f, appointmentTime: e.target.value }))} className="input w-full">
              <option value="">Select a slot</option>
              {APPOINTMENT_SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">PO Number</label>
            <input value={form.poNumber} onChange={(e) => setForm(f => ({ ...f, poNumber: e.target.value }))} placeholder="Purchase order no." className="input w-full" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">PO Expiry Date</label>
            <input type="date" value={form.poExpiryDate} onChange={(e) => setForm(f => ({ ...f, poExpiryDate: e.target.value }))} className="input w-full" />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-medium text-slate-500 mb-1 block">Appointment ID</label>
            <input value={form.appointmentRef} onChange={(e) => setForm(f => ({ ...f, appointmentRef: e.target.value }))} placeholder="Leave blank for a new booking; enter an existing ID to re-book that slot" className="input w-full" />
          </div>
        </div>

        {/* Push to the carrier. Offered only where an appointment API exists (Delhivery B2B today) —
            elsewhere this screen is our own tracker and there is nobody to send the slot to. */}
        {status === 'found' && card?.supportsCourierBooking && (
          <label className="flex items-start gap-2 rounded-lg border border-indigo-200 bg-indigo-50 p-3 cursor-pointer">
            <input type="checkbox" className="mt-0.5" checked={pushToCourier} onChange={(e) => setPushToCourier(e.target.checked)} />
            <span className="text-xs text-indigo-900">
              <span className="font-medium">Also book this with {card?.courier?.name ?? 'the courier'}</span>
              <span className="block text-indigo-700 mt-0.5">
                Sends the slot to their appointment API. PO Number and <strong>PO Expiry Date are required</strong> by
                Delhivery — without them they refuse the booking. The appointment is saved here either way.
              </span>
            </span>
          </label>
        )}

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-100">
          {pushNeedsPo && (
            <span className="text-xs text-amber-700 mr-auto">
              <span className="font-semibold">Needed to book with the courier:</span>{' '}
              {[!form.appointmentTime && 'Appointment Time', !form.poExpiryDate && 'PO Expiry Date'].filter(Boolean).join(', ')}
              {' '}— or untick the courier booking to save locally only.
            </span>
          )}
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">CANCEL</button>
          <button onClick={() => save.mutate()} disabled={!canSave}
            className="px-5 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2">
            {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <CalendarClock size={14} />} Save Appointment
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Info({ label, value, icon }: { label: string; value?: string; icon?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-slate-400">{label}:</span>
      <span className="font-medium text-slate-700 flex items-center gap-1 truncate">{icon}{value || '—'}</span>
    </div>
  );
}

/**
 * Why does this consignment not need a delivery slot?
 *
 * Asked at the moment the answer is known, because choosing No takes the row off every working list
 * and a month later nobody remembers the reason. The limit is 200 WORDS, counted as words because
 * that is what was asked for, and the count is shown live rather than silently truncating.
 */
/**
 * Hand this consignment to a third party — a porter service, the consignee's own transporter.
 *
 * Asks for WHO and WHEN before the row leaves the working lists, because that is the whole value of
 * the tab it moves to: a list of "someone else is doing it" that cannot say who, or by when, is
 * worse than leaving the row on Pending where at least somebody will chase it.
 *
 * Remarks are optional and deliberately so — the title and the slot are the facts, the remark is
 * context, and a blocked field gets "." typed into it within a week.
 */
function ThirdPartyDialog({ row, busy, fromTab, onCancel, onConfirm }: {
  row: Appt;
  /** The tab the switch was pressed on — the row leaves THAT list. */
  fromTab?: string;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (v: { title: string; date: string; time: string; remarks?: string }) => void;
}) {
  const [title, setTitle] = useState(row.thirdPartyTitle ?? '');
  // A booked row already has a day agreed — start from it rather than an empty box.
  const [date, setDate] = useState(row.thirdPartyDate ? String(row.thirdPartyDate).slice(0, 10)
    : row.appointmentDate ? String(row.appointmentDate).slice(0, 10) : '');
  // Only a plain HH:MM fits the time box — a slot such as "10:00-12:00" is left for the user to pick.
  const [time, setTime] = useState(row.thirdPartyTime
    ?? (/^\d{2}:\d{2}$/.test(String(row.appointmentTime ?? '')) ? String(row.appointmentTime) : ''));
  const [remarks, setRemarks] = useState(row.thirdPartyRemarks ?? '');
  const words = remarks.trim() ? remarks.trim().split(/\s+/).length : 0;
  const over = words > 200;
  const ready = !!title.trim() && !!date && !!time && !over;

  return (
    <div className="fixed inset-0 z-[1100] bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onCancel}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <div>
            <h3 className="font-semibold text-slate-800">Third party appointment</h3>
            <p className="text-xs text-slate-500 font-mono">{row.lrNo}</p>
          </div>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-3">
          <div className="rounded-lg bg-indigo-50 border border-indigo-200 px-3 py-2 text-[12px] text-indigo-900">
            This moves the consignment to <b>Third Party Appointments</b> and off {fromTab === 'booked' ? 'Booked' : fromTab === 'failed' ? 'Appointment Failed' : 'Pending'} — nobody here will
            chase the slot after this, so record who is handling it and when.
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Who is handling it *</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} autoFocus
              placeholder="e.g. Porter service" className="input w-full" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Date *</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input w-full" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Time *</label>
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="input w-full" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Remarks (optional)</label>
            <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={3}
              placeholder="Anything the desk should know" className="input w-full resize-none" />
            <div className={`text-[11px] mt-0.5 ${over ? 'text-rose-600 font-medium' : 'text-slate-400'}`}>
              {words} / 200 words{over ? ' — too long' : ''}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-100">
          <button onClick={onCancel} className="px-4 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50">Cancel</button>
          <button onClick={() => onConfirm({ title: title.trim(), date, time, remarks: remarks.trim() || undefined })}
            disabled={!ready || busy}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {busy ? 'Saving…' : 'Move to Third Party'}
          </button>
        </div>
      </div>
    </div>
  );
}

function NotRequiredDialog({ row, busy, onCancel, onConfirm }: {
  row: Appt; busy?: boolean; onCancel: () => void; onConfirm: (remarks: string) => void;
}) {
  const [text, setText] = useState('');
  const words = text.trim() ? text.trim().split(/\s+/).filter(Boolean).length : 0;
  const tooLong = words > 200;
  return (
    <div className="fixed inset-0 z-[1100] bg-black/40 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-100">
          <h3 className="font-semibold text-slate-800">Appointment not required</h3>
          <p className="text-xs text-slate-500 font-mono">{row.lrNo}</p>
        </div>
        <div className="p-5">
          <label className="block text-xs font-medium text-slate-600 mb-1">Why does this one not need an appointment?</label>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} autoFocus
            placeholder="e.g. consignee accepts walk-in delivery, no PO required"
            className={`input w-full resize-none ${tooLong ? 'border-rose-400' : ''}`} />
          <div className={`text-[11px] mt-1 ${tooLong ? 'text-rose-600 font-medium' : 'text-slate-400'}`}>
            {words} / 200 words{tooLong ? ' — too long, shorten it' : ''}
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            This moves the shipment to the <b>Appointment not required</b> tab, where your note is shown beside it.
          </p>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-100">
          <button onClick={onCancel} className="px-4 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50">Cancel</button>
          <button onClick={() => onConfirm(text.trim())} disabled={busy || tooLong}
            className="px-4 py-2 bg-slate-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {busy ? 'Saving…' : 'Mark not required'}
          </button>
        </div>
      </div>
    </div>
  );
}
